import { View } from "react-native";
import Svg, { Line } from "react-native-svg";

import { pictureScaleOf } from "../pictureScale";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import type { UiTheme } from "../theme";
import type { ChartVariant } from "./chartAnnotations";
import { lineColor, lineDashArray, lineStroke, lineWidth, type LineStroke } from "./chartDots";

// #389: a short sample of a curve's line, drawn with the same colour, width
// and dash pattern as the curve (chartDots.ts), so the key cannot show a line
// the chart does not draw. The tracker's key and WagmiChartLegend both use it.
// A colour that is not ink sits on a 1px ink outline (a wider ink stroke under
// it, same dash), so the swatch's edge keeps >=3:1 against the card in every
// theme: brand500 alone is 2.74:1 on rosepine light. Ink is >=7.69:1 on every
// surface (13.69 metria light, 7.69 catppuccin light). The chart's own line is
// untouched: only the key needs the edge.

export function LineSwatch({
  theme,
  variant = "primary",
  stroke,
}: {
  theme: UiTheme;
  variant?: ChartVariant;
  stroke?: LineStroke;
}) {
  const k = pictureScaleOf(theme);
  const width = 28 * k;
  const height = 12 * k;
  const strokeWidth = lineWidth(variant, k);
  const dash = lineDashArray(lineStroke(variant, stroke), strokeWidth, k);
  const color = lineColor(theme, variant);
  const outline = color === theme.palette.ink ? 0 : 2 * k;
  const inset = (strokeWidth + outline) / 2;
  const line = (w: number, c: string) => ({
    x1: inset,
    y1: height / 2,
    x2: width - inset,
    y2: height / 2,
    stroke: c,
    strokeWidth: w,
    strokeLinecap: "round" as const,
    ...(dash ? { strokeDasharray: dash } : {}),
  });
  return (
    // Decorative: the name beside it says which curve it is.
    <View {...hiddenFromScreenReader()}>
      <Svg width={width} height={height}>
        {outline ? <Line {...line(strokeWidth + outline, theme.palette.ink)} /> : null}
        <Line {...line(strokeWidth, color)} />
      </Svg>
    </View>
  );
}
